import {
  createRecentFlagStore,
  createSessionJsonStore,
  createSsoSessionStore,
} from "@airange/shared/session";
import type { AuthLoginSource } from "@airange/shared/auth";
import type { UserSession } from "../types/auth";
import { invalidateAllRequestCaches } from "./request-cache";

const ACCESS_TOKEN_KEY = "airange.admin.access_token";
const USER_KEY = "airange.admin.user";
const AUTH_SOURCE_KEY = "airange.admin.auth_source";
const SSO_NO_ACCESS_USER_KEY = "airange.admin.sso_no_access_user";
const RECENT_SSO_LOGOUT_AT_KEY = "airange.admin.recent_sso_logout_at";
const RECENT_SSO_LOGOUT_TTL_MS = 60 * 1000;

export type AuthMode = "local" | "hybrid" | "sso";
export type { AuthLoginSource } from "@airange/shared/auth";

const sessionStore = createSsoSessionStore<UserSession, AuthLoginSource>({
  accessTokenKey: ACCESS_TOKEN_KEY,
  authSourceKey: AUTH_SOURCE_KEY,
  clearExtras: () => {
    setStoredSsoNoAccessUser(null);
    clearImpersonationActorSession();
  },
  userKey: USER_KEY,
});

const recentSsoLogoutStore = createRecentFlagStore({
  key: RECENT_SSO_LOGOUT_AT_KEY,
  storage: typeof window === "undefined" ? undefined : window.localStorage,
  ttlMs: RECENT_SSO_LOGOUT_TTL_MS,
});

const ssoNoAccessUserStore = createSessionJsonStore<UserSession>({
  key: SSO_NO_ACCESS_USER_KEY,
});

export const {
  clearStoredAuthSource,
  clearAccessToken,
  getAccessToken,
  getStoredAuthSource,
  getStoredUser,
  isAuthenticated,
  setStoredAuthSource,
} = sessionStore;

export const setAccessToken = sessionStore.setAccessToken;

export function setSession(accessToken: string, user: UserSession) {
  invalidateAllRequestCaches();
  sessionStore.setSession(accessToken, user);
}

export function setStoredUser(user: UserSession) {
  invalidateAllRequestCaches();
  sessionStore.setStoredUser(user);
}

export function clearStoredAccessToken() {
  clearAccessToken();
}

type StoredImpersonationActorSession = {
  access_token: string;
  user: UserSession;
  auth_source: AuthLoginSource;
};

let impersonationActorSession: StoredImpersonationActorSession | null = null;

export function getStoredSsoNoAccessUser(): UserSession | null {
  return ssoNoAccessUserStore.get();
}

export function setStoredSsoNoAccessUser(user: UserSession | null | undefined) {
  ssoNoAccessUserStore.set(user);
}

export function consumeStoredSsoNoAccessUser(): UserSession | null {
  return ssoNoAccessUserStore.consume();
}

export function getStoredImpersonationActorSession(): StoredImpersonationActorSession | null {
  return impersonationActorSession;
}

export function stashImpersonationActorSession() {
  const accessToken = getAccessToken();
  const user = getStoredUser();
  const authSource = getStoredAuthSource();
  if (!accessToken || !user || !authSource) {
    return;
  }
  impersonationActorSession = {
    access_token: accessToken,
    user,
    auth_source: authSource,
  };
}

export function restoreImpersonationActorSession() {
  const payload = getStoredImpersonationActorSession();
  if (!payload) {
    return false;
  }
  setSession(payload.access_token, payload.user);
  setStoredAuthSource(payload.auth_source);
  clearImpersonationActorSession();
  return true;
}

export function clearImpersonationActorSession() {
  impersonationActorSession = null;
}

export function clearSession() {
  invalidateAllRequestCaches();
  sessionStore.clearSession();
}

export function markRecentSsoLogout(at = Date.now()) {
  recentSsoLogoutStore.mark(at);
}

export function consumeRecentSsoLogout(ttlMs = RECENT_SSO_LOGOUT_TTL_MS) {
  return recentSsoLogoutStore.consume(ttlMs);
}
